<template>
  <component
    :is="isBoxCatalog ? 'div' : 'router-link'"
    class="box-card"
    :class="{ '--set-height': !isBoxCatalog }"
    :to="boxLink"
    :target="isBoxCatalog || isBoxLanding || isBoxHomeView || isRetailMode ? '' : '_blank'"
    :rel="
      isBoxCatalog || isBoxLanding || isBoxHomeView || isRetailMode ? '' : 'noopener noreferrer'
    "
    @click="handleClick"
  >
    <div class="product-image-container">
      <div class="image" :style="`background-image: url(${optimizedImageUrl})`">
        <div v-if="displayTag" class="tag">
          <div class="tag__icon" v-html="displayTag.icon" />
          <span class="typography-action-XSmall">{{ displayTag.message }}</span>
        </div>
        <HeartIt v-if="!isRetailMode" :product="box" class="hearth-icon" />
      </div>
    </div>
    <div class="title">
      <h3
        :class="[
          'title__name',
          'typography-title-medium-2',
          rating > 0 ? '' : 'title__name--large',
        ]"
      >
        {{ box.name }}
      </h3>
      <div v-if="rating > 0" class="rating typography-body-XSmall">
        <span
          class="rating__icon"
          v-html="icons.star"
          aria-hidden="true"
        ></span>
        <span class="typography-body-XSmall" aria-label="Rating">{{
          rating
        }}</span>
      </div>
    </div>
    <v-clamp
      class="description typography-body-small"
      autoresize
      :max-lines="2"
    >
      {{ boxDescription }}</v-clamp
    >

    <div class="info">
      <div v-for="(item, index) in infoItems" :key="index" class="info__item">
        <div class="icon" v-html="item.icon" />
        <span
          class="message typography-body-XSmall"
          :class="{ '--ellipsis': item.showEllipsis }"
          >{{ item.message }}</span
        >
      </div>
    </div>
    <div v-if="!isBoxCatalog" class="price">
      <span class="typography-title-small-1">{{ price }}</span>
      <span
        v-if="isOnSale"
        class="price__without-discount typography-subtitle-small-4"
      >
        {{ currency(originalPrice) }}
      </span>
      <div v-if="isOnSale" class="discount-badge">
        <span class="discount-badge__text typography-action-XSmall">
          {{ discount }}% Off
        </span>
      </div>
    </div>
    <div
      v-if="box.available_hours && box.available_hours.length > 0"
      class="slider-wrapper"
    >
      <activity-card-hours
        :availableHours="box.available_hours"
        :activitySlug="box.slug"
      />
    </div>
  </component>
</template>

<script>
  import Vue from 'vue';
  import Component from 'vue-class-component';
  import Star from 'common/shop/assets/icons/Star2.svg';
  import Users from 'common/shop/assets/icons/Users.svg';
  import Options from 'common/shop/assets/icons/Options.svg';
  import Gift2 from 'common/shop/assets/icons/Gift2.svg';
  import Spark from 'common/shop/assets/icons/Spark.svg';
  import Crown from 'common/shop/assets/icons/Crown.svg';
  import Bell from 'common/shop/assets/icons/Bell.svg';
  import Truck from 'common/shop/assets/icons/Truck.svg';
  import House2 from 'common/shop/assets/icons/House2.svg';
  import Bag2 from 'common/shop/assets/icons/Bag2.svg';
  import Notebook from 'common/shop/assets/icons/Notebook.svg';
  import MapPin from 'common/shop/assets/icons/MapPin.svg';
  import NowIcon from 'common/assets/icons/now-icon.svg';
  import currency from 'common/filters/currency';
  import HeartIt from 'common/components/HeartIt';
  import ActivityCardHours from '../../../ecommerce/components/ActivityCardHours/ActivityCardHours.vue';
  import useFilters from '../../../ecommerce/composables/useFilters';
  import useCatalogFilters from '../../../ecommerce/composables/useCatalogFilters';
  import CatalogPreviewModal from '../../../ecommerce/components/Catalog/CatalogPreviewModal/CatalogPreviewModal.vue';
  import VClamp from 'vue-clamp';
  import optimizeImage from 'common/utils/optimizeImage';

  @Component({
    props: {
      box: {
        type: Object,
        required: true,
      },
      category: {
        type: Object,
        required: false,
      },
      landingSlug: {
        type: String,
        required: false,
      },
      applyFilters: {
        type: Boolean,
        required: false,
        default: false,
      },
      isRetailMode: {
        type: Boolean,
        required: false,
        default: false,
      },
      isBoxLanding: {
        type: Boolean,
        required: false,
        default: false,
      },
      isBoxHomeView: {
        type: Boolean,
        required: false,
        default: false,
      },
    },
    components: {
      CatalogPreviewModal,
      HeartIt,
      ActivityCardHours,
      VClamp,
    },
    setup() {
      const { getCurrentFilters } = useFilters();
      const { onToggleModalPreview, getModalPreviewFlag } = useCatalogFilters();
      return {
        onToggleModalPreview,
        showPreviewModal: getModalPreviewFlag,
        getCurrentFilters,
      };
    },
  })
  export default class BoxCardComponent extends Vue {
    icons = {
      users: Users,
      options: Options,
      gift2: Gift2,
      star: Star,
      spark: Spark,
      crown: Crown,
      bell: Bell,
      truck: Truck,
      house2: House2,
      bag2: Bag2,
      notebook: Notebook,
      mapPin: MapPin,
      now: NowIcon,
    };

    tags = {
      new: {
        message: 'Nuevo',
        icon: this.icons.spark,
      },
      onlineReservation: {
        message: 'Reserva Online',
        icon: this.icons.bell,
      },
      delivery: {
        message: 'Delivery',
        icon: this.icons.truck,
      },
      homeExperience: {
        message: 'Experiencia en casa',
        icon: this.icons.house2,
      },
      takeAway: {
        message: 'Take Away',
        icon: this.icons.bag2,
      },
      onlineExperience: {
        message: 'Experiencia online',
        icon: this.icons.notebook,
      },
      onlyWithUs: {
        message: 'Only with us',
        icon: this.icons.crown,
      },
      purchaseReservation: {
        message: `${this.$translate('Reservá')} ahora`,
        icon: this.icons.now,
      },
    };

    infoItems = [];
    displayTag = null;

    get isBoxCatalog() {
      return Boolean(
        this.$route.name === 'box-catalog' || this.$route.name === 'box',
      );
    }

    created() {
      this.setInitialInfoItems();
    }
    mounted() {
      switch (true) {
        case this.box.hasOwnProperty('is_new'):
          if (this.box.is_new) {
            this.displayTag = this.tags.new;
          }
        case this.box.hasOwnProperty('is_remote'):
          if (this.box.is_remote) {
            this.displayTag = this.tags.onlineExperience;
          }
        case this.box.partneractivity?.hasOwnProperty(
          'has_online_reservation',
        ) === true:
          if (this.box.partneractivity?.has_online_reservation) {
            this.displayTag = this.tags.onlineReservation;
          }
        case this.box.partneractivity?.hasOwnProperty(
          'can_purchase_a_reservation',
        ) === true:
          if (
            this.box.partneractivity?.can_purchase_a_reservation &&
            this.box.partneractivity?.can_purchase_a_reservation !==
              'INACTIVE' &&
            !this.isRetailMode
          ) {
            this.displayTag = this.tags.purchaseReservation;
          }
        // TODO: Add the next tags
        // delivery
        // homeExperience
        // takeAway
        // onlyWithUs
      }
    }
    setInitialInfoItems() {
      const { __typename, activity_qty, participants } = this.box;
      const isBoxType = __typename === 'BoxType';
      const isMultipleParticipant = participants > 1;
      this.infoItems = [
        {
          icon: isBoxType ? this.icons.options : this.icons.mapPin,
          message: isBoxType
            ? `Contiene ${activity_qty} opciones`
            : this.locations,
          showEllipsis: !isBoxType,
        },
        {
          icon: this.icons.users,
          message: isBoxType
            ? participants
            : `Para ${participants} ${
                isMultipleParticipant ? 'personas' : 'persona'
              }`,
          showEllipsis: false,
        },
      ];
    }
    get type() {
      if (this.box.__typename === 'BoxType') {
        return 'box';
      } else if (this.box?.public_boxes && this.box?.public_boxes?.length > 0) {
        return 'boxActivity';
      } else {
        return 'individualActivity';
      }
    }

    get currentProduct() {
      // Para boxActivity, retorna el primer producto público disponible
      if (this.type === 'boxActivity' && this.box?.public_boxes?.length > 0) {
        const textDescription = this.box.short_description;
        return { textDescription, ...this.box.public_boxes[0] };
      }

      // Para individualActivity, retorna el producto individual
      if (this.type === 'individualActivity') {
        const textDescription = this.box.short_description;
        return { textDescription, ...this.box };
      }

      // Para box (BoxType), retorna el box directamente
      if (this.type === 'box') {
        const textDescription = this.box.catalog_description;
        return { textDescription, ...this.box };
      }

      // Fallback: retorna el box original si no se puede determinar el tipo
      return this.box;
    }

    get price() {
      if (this.currentProduct?.is_consultation) {
        const price = this.currentProduct?.currency_symbol
          ? `${this.currentProduct?.currency_symbol} ${this.currentProduct?.price}`
          : currency(this.currentProduct?.price);
        return this.currentProduct?.price_from ? `Desde ${price}` : price;
      }

      return currency(this.currentProduct?.price);
    }

    get originalPrice() {
      return this.type === 'individualActivity'
        ? this.currentProduct?.price_without_discount
        : this.currentProduct?.original_price;
    }

    get discount() {
      return this.type === 'individualActivity'
        ? Number(this.currentProduct?.discount_percentage)
        : this.currentProduct?.discount;
    }

    get isOnSale() {
      return !!this.discount;
    }

    get rating() {
      return Number(this.box.rating).toFixed(1);
    }

    get locations() {
      return this.formatLocationText();
    }

    get isOnlineActivity() {
      return this.box.partneractivity?.activity_type === 'online';
    }

    formatLocationText() {
      const { partneractivity, comboactivity } = this.box;

      const hasMultipleLocations = partneractivity =>
        partneractivity?.locations?.length > 1;

      const hasSingleLocation = partneractivity =>
        partneractivity?.locations?.length === 1;

      const formatSingleLocation = location =>
        `${location.sublocation}, ${location.province.name}`;

      const formatComboActivityLocations = locations => {
        let formatter = new Intl.ListFormat('es', { type: 'unit' });
        let uniqueLocations = locations
          .map(location => location.sublocation)
          .filter(
            (location, index, array) => array.indexOf(location) === index,
          );
        uniqueLocations.splice(4);
        return `${formatter.format(uniqueLocations)}...`;
      };

      if (this.isOnlineActivity) {
        return 'Disponible online';
      } else if (hasMultipleLocations(partneractivity)) {
        return 'Múltiples localidades';
      } else if (hasSingleLocation(partneractivity)) {
        return formatSingleLocation(partneractivity.locations[0]);
      } else if (comboactivity) {
        return formatComboActivityLocations(comboactivity.locations);
      }
      return '';
    }

    handleClick() {
      if (this.isBoxCatalog) {
        this.onToggleModalPreview(this.box);
      } else {
        return;
      }
    }

    get boxLink() {
      const query = this.applyFilters &&
        this.getCurrentFilters &&
        this.getCurrentFilters.length && {
          currentFilters: JSON.stringify([
            {
              ...this.getCurrentFilters[0],
              category_name: [],
            },
          ]),
        };

      let newRoute;
      //USING SAME COMPONENT FOR BOXES AND ACTIVITIES
      //TODO: REFACTOR THIS TO USE A DIFFERENT COMPONENT FOR ACTIVITIES
      if (this.type === 'boxActivity' && this.category) {
        newRoute = {
          name: 'boxActivity',
          params: {
            categorySlug: this.category.slug,
            boxSlug: this.currentProduct.slug,
            activitySlug: this.box.slug,
          },
        };
      } else if (this.type === 'individualActivity') {
        newRoute = {
          name: 'activity',
          params: {
            activitySlug: this.currentProduct.slug,
          },
        };
      } else if (this.type === 'box') {
        if (this.currentProduct.product_url) {
          newRoute = {
            path: this.currentProduct.product_url,
            query,
          };
        } else {
          newRoute = {
            name: 'box',
            params: {
              categorySlug: this.category.slug,
              boxSlug: this.currentProduct.slug,
            },
            query,
          };
        }
      }

      if (this.campaignSlug) {
        newRoute.query = {
          ...newRoute.query,
          campaignSlug: this.campaignSlug,
        };
      }
      let landingSlug = this.landingSlug || this.$route.params.landingSlug;
      if (landingSlug) {
        newRoute.query = {
          ...newRoute.query,
          landingSlug: landingSlug,
        };
      }
      // Add retail=true query parameter when isRetailMode is true
      if (this.isRetailMode) {
        newRoute.query = {
          ...newRoute.query,
          retail: 'true',
        };
      }
      return newRoute;
    }

    get boxDescription() {
      return (
        this.currentProduct.textDescription ||
        this.currentProduct.catalog_description
      );
    }

    get optimizedImageUrl() {
      // 576 equals 2x max-width of the box-card
      return optimizeImage(this.box.product_image_url, '576');
    }
  }
</script>

<style lang="scss">
  @import '~assets/styles/index.scss';

  .box-card {
    background-color: white;
    border: 1px solid #f2f2f2;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    height: auto;
    max-width: 288px;
    min-width: 140px;
    padding: 12px;
    position: relative;
    cursor: pointer;

    &.--set-height {
      min-height: 440px;

      @media (max-width: $screen-md-max) {
        height: 440px;
        min-height: auto;
      }

      @media (max-width: $screen-xs-max) {
        height: 380px;
      }
    }

    .image {
      width: 100%;
      max-height: 228px;
      aspect-ratio: 1/1;
      border-radius: 8px;
      margin-bottom: 12px;
      background-position: center;
      background-repeat: no-repeat;
      background-size: cover;
      position: relative;

      @media (max-width: $screen-xs-max) {
        max-height: 178px;
      }

      .tag {
        padding: 1px 6px;
        margin-top: 12px;
        margin-left: 6px;
        background-color: white;
        border-radius: 8px;
        display: inline-flex;
        flex-flow: row nowrap;
        align-items: center;
        justify-content: flex-start;

        &__icon {
          width: 16px;
          height: 16px;
          margin-right: 4px;
        }
      }

      .hearth-icon {
        width: 24px;
        height: 24px;
        position: absolute;
        top: 8px;
        right: 8px;
      }
    }

    .title {
      margin-bottom: 8px;
      display: flex;
      flex-flow: row nowrap;
      justify-content: space-between;
      align-items: center;
      height: 44px;

      &__name {
        overflow: hidden;
        max-width: 200px;
        color: #222222;
        max-height: 44px;

        &--large {
          max-width: none;
        }
      }

      .rating {
        color: #222222;
        display: flex;
        flex-flow: row nowrap;

        &__icon {
          height: 16px;
          width: 16px;
          margin-right: 2px;
        }
      }
    }

    .description {
      margin-bottom: 8px;
      color: #828282;
      max-height: 38px;
      overflow: hidden;
      height: -webkit-fill-available;
    }

    .info {
      margin-bottom: 12px;
      display: flex;
      flex-flow: column nowrap;

      &__item {
        margin-bottom: 4px;
        padding: 0 2px;
        display: flex;
        flex-flow: row nowrap;
        align-items: center;
        gap: 8px;

        &:last-child {
          margin-bottom: 0;
        }

        .icon {
          width: 16px;
          height: 16px;
          flex: 1 0 auto;
          max-width: 16px;

          svg {
            height: 100%;
            width: 100%;
          }
        }

        .message {
          color: #222222;

          &.--ellipsis {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }
      }
    }

    .price {
      display: flex;
      align-items: flex-end;
      flex: 1;
      gap: 8px;
      flex-wrap: nowrap;

      &__without-discount {
        color: #828282;
        text-decoration: line-through;
      }
    }

    .product-image-container {
      position: relative;
    }

    .discount-badge {
      background-color: #f6fefc;
      border-radius: 4px;
      padding: 2px 8px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      height: fit-content;

      &__text {
        color: #13866a;
        font-weight: 600;
        font-size: 12px;
        line-height: 16px;
      }
    }
  }
</style>
